import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';
import { Checkbox, Button, CircularProgress } from "@mui/material";

import { GET_ALL_PARENT } from '../../../helpers/api/Queries.js';
import { UPDATE_SUBSCRIPTION_USER } from '../../../helpers/api/Mutation.js';
import { QueryRequest, MutateRequest } from '../../../helpers/api/service_helper.js';
import { notifyError, notifySuccess } from "../../../common/notifications.js";
import { ToastContainer } from 'react-toastify';

const Subscription = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [changedData, setChangedData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    // Controlled pagination state
    const [pagination, setPagination] = useState({
        pageIndex: 0, // Page starts from 0
        pageSize: 10,  // Customize the default page size
    });

    // Fetch data based on pagination
    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await QueryRequest(GET_ALL_PARENT, {
                searchString: "",
                pageNumber: pagination.pageIndex + 1, // Convert 0-based index to 1-based
                pageLimit: pagination.pageSize,
            });

            const { items, pagination: responsePagination } = response.data.getAllParents;
            setTotalRecords(responsePagination.totalCount || 0);

            setData(items || []);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [pagination]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubscriptionChange = (id) => {
        setData((prevData) =>
            prevData.map((item) => {
                if (item.id === id) {
                    const updatedItem = { ...item, subscribed: !item.subscribed };

                    setChangedData((prevChanged) => {
                        const existingIndex = prevChanged.findIndex(changed => changed.id === id);
                        if (existingIndex !== -1) {
                            const updatedChanges = [...prevChanged];
                            updatedChanges[existingIndex] = updatedItem;
                            return updatedChanges;
                        }
                        return [...prevChanged, updatedItem];
                    });

                    return updatedItem;
                }
                return item;
            })
        );
    };
    const handleSave = async () => {
        try {
            if (changedData.length === 0) {
                notifyError("No changes to save");
                return;
            }
            setIsLoading(true)
            const filteredData = changedData.map(({ id, userId, subscribed, mobileNumber }) => ({
                parentId: id,
                userId: userId,
                subscribed: subscribed,
                mobileNumber: mobileNumber
            }));

            const response = await MutateRequest(UPDATE_SUBSCRIPTION_USER, {
                data: filteredData,
            });
            if (response?.data?.updateSubscription?.status === "Success") {
                notifySuccess("Updated successfully");
                fetchData(); // Refresh table data
                setChangedData([]); // Clear after saving
            } else {
                notifyError("Error while updating");
            }
        } catch (error) {
            console.error("Error in handleSave:", error);
            notifyError("Something went wrong while saving.");
        } finally {
            setIsLoading(false)
        }
    };


    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                Edit: () => null,
                enableEditing: false,
                hidden: true,
                enableHiding: true,
                visibleInShowHideMenu: false,
                columnVisibility: false
            },
            {
                accessorKey: 'userId',
                header: 'User ID',
                Edit: () => null,
                enableEditing: false,
                hidden: true,
                enableHiding: true,
                visibleInShowHideMenu: false,
                columnVisibility: false
            },
            {
                accessorKey: 'name',
                header: 'Parent Name',
                size: 80,
                enableEditing: false,
            },
            {
                accessorKey: 'mobileNumber',
                header: 'Parent Phone Number',
                enableEditing: false,
            },
            {
                accessorKey: 'subscribed',
                header: 'Subscribe',
                Cell: ({ row }) => (
                    <Checkbox
                        checked={row.original.subscribed}
                        onChange={() => handleSubscriptionChange(row.original.id)}
                    />
                ),
            },
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data,
        initialState: { columnVisibility: { id: false, userId: false } },
        editDisplayMode: 'modal',
        muiPaginationProps: {
            rowsPerPageOptions: [10, 50, 100, 500, 1000, 1500, 2000],
        },
        muiTablePaperProps: ({ table }) => ({
            style: {
                zIndex: table.getState().isFullScreen ? 1000 : undefined,
            },
        }),
        renderTopToolbarCustomActions: ({ table }) => (
            <Button
                variant="contained"
                onClick={handleSave}
                sx={{ backgroundColor: 'rgb(30 58 138)' }}

            >
                Save
            </Button>
        ),
        state: { pagination, isLoading },
        onPaginationChange: setPagination, // Update pagination state
        manualPagination: true, // Tell the table pagination is controlled manually
        rowCount: totalRecords, // Total number of records
    });

    return (
        <>
            <ToastContainer />
            <MaterialReactTable table={table} />
        </>
    );
};

export default Subscription;
